/**
 * A workflow's run history.
 *
 * Skipped files are shown as a count that expands into the reasons. The reasons
 * are the point: a run that processed 34 of 36 files without saying why is
 * indistinguishable from one that lost two, and this table is the only place a
 * user finds out.
 */

import { ChevronDown, ChevronRight, Undo2, XCircle } from "lucide-react";
import React, { useState } from "react";
import {
	RUN_STATUS_LABELS,
	RUN_TRIGGER_LABELS,
	isTerminalRunStatus,
	type RunStatus,
	type WorkflowRun,
} from "../../types/kpiWorkflow";

interface RunHistoryTableProps {
	runs: WorkflowRun[];
	/**
	 * Stop one run. A run is what gets cancelled — not a workflow — so the
	 * affordance belongs on the row that identifies it, beside the status that
	 * says it is still going.
	 */
	onCancelRun?: (runId: string) => void;
	/**
	 * Set the table to what it showed when a run finished. Each finished run is
	 * a version of the table; the server says which is current and which others
	 * can be restored (`is_current`, `can_restore`), and this is offered on
	 * exactly those rows. Settles when the restore has, so the row can say it is
	 * working until then.
	 */
	onRestoreRun?: (runId: string) => Promise<void>;
}

const STATUS_CLASS: Record<RunStatus, string> = {
	queued: "bg-gray-100 text-gray-700",
	running: "bg-blue-50 text-blue-700",
	completed: "bg-emerald-50 text-emerald-700",
	failed: "bg-red-50 text-red-700",
	// Grey, not red: a cancelled run is not a fault, and colouring it like one
	// would suggest the values it wrote are suspect.
	cancelled: "bg-gray-100 text-gray-700",
};

/** A queued run has no start time at all — say so rather than inventing one. */
const formatTimestamp = (ms: number | null): string =>
	ms === null
		? "Not started"
		: new Date(ms).toLocaleString(undefined, {
				month: "short",
				day: "numeric",
				hour: "numeric",
				minute: "2-digit",
			});

const formatDuration = (run: WorkflowRun): string => {
	if (run.finished_at_ms === null || run.started_at_ms === null) return "—";
	const seconds = Math.max(
		0,
		Math.round((run.finished_at_ms - run.started_at_ms) / 1000),
	);
	if (seconds < 60) return `${seconds}s`;
	return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/**
 * What the run did to the numbers, or an admission that nobody knows.
 *
 * Runs from before value stamping report nothing, and this must not round that
 * down to "0 revised" — the user would read a run that changed figures as one
 * that changed none.
 */
const formatValues = (run: WorkflowRun): string => {
	if (run.values_written === null) return "Not recorded";
	if (run.values_revised === null || run.values_revised === 0) {
		return `${run.values_written} written`;
	}
	return `${run.values_written} written · ${run.values_revised} revised`;
};

const RunRow: React.FC<{
	run: WorkflowRun;
	onCancelRun?: (runId: string) => void;
	/** Present only on rows whose version the table can be set to. */
	onRestoreRun?: (runId: string) => Promise<void>;
}> = ({ run, onCancelRun, onRestoreRun }) => {
	const [showSkipped, setShowSkipped] = useState(false);
	const [showError, setShowError] = useState(false);
	// Asked twice, in the row: restoring changes the numbers on the table, and
	// a native confirm would block the page to say so.
	const [confirmingRestore, setConfirmingRestore] = useState(false);
	const [restoring, setRestoring] = useState(false);
	const restore = async () => {
		if (!onRestoreRun) return;
		setRestoring(true);
		try {
			await onRestoreRun(run.id);
		} finally {
			setRestoring(false);
			setConfirmingRestore(false);
		}
	};
	const hasSkips = run.skipped.length > 0;
	// Only a run that has not settled can be stopped. Offering it on a finished
	// one would promise something the backend answers with a 409.
	const canCancel = Boolean(onCancelRun) && !isTerminalRunStatus(run.status);

	return (
		<>
			{/* A run whose results were set aside is dimmed rather than badged: it
			    is history, and the row should read as quieter than the versions
			    still in effect. */}
			<tr
				className={`border-t border-gray-100 ${run.undone ? "opacity-60" : ""}`}
				title={
					run.undone
						? "This run's results are not in use: an earlier version was restored"
						: undefined
				}
			>
				<td className="px-3 py-2">
					{/* The status is the answer; why a run failed is detail, one
					    click away rather than on a row of its own. A button, not a
					    hover title, so keyboard and touch can reach it too. */}
					{run.error ? (
						<button
							type="button"
							onClick={() => setShowError((open) => !open)}
							aria-expanded={showError}
							title="Show why this run failed"
							className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[run.status]}`}
						>
							{RUN_STATUS_LABELS[run.status]}
						</button>
					) : (
						<span
							className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[run.status]}`}
						>
							{RUN_STATUS_LABELS[run.status]}
						</span>
					)}
					{run.undone && <span className="sr-only">Not in use</span>}
					{/* Beside the status, not instead of it: the run did finish. This
					    says which version the table is showing. */}
					{run.is_current && (
						<span
							className="ml-1.5 inline-block rounded-full bg-[#008782]/10 px-2 py-0.5 text-xs font-medium text-[#008782]"
							title="The table is showing this run's results"
						>
							Current
						</span>
					)}
				</td>
				<td className="px-3 py-2 text-sm text-text-muted">
					{RUN_TRIGGER_LABELS[run.trigger]}
					{/* Quiet, but present: a forced run re-read files it had already
					    extracted, which is what explains its cost and its counts. */}
					{run.forced && (
						<span
							className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-text-muted"
							title="Re-read every file, including ones already extracted"
						>
							Forced
						</span>
					)}
				</td>
				<td className="px-3 py-2 text-sm text-text-base">
					{run.files_processed} / {run.files_total}
				</td>
				<td className="px-3 py-2 text-sm text-text-muted whitespace-nowrap">
					{formatValues(run)}
				</td>
				<td className="px-3 py-2 text-sm">
					{hasSkips ? (
						<button
							type="button"
							onClick={() => setShowSkipped((open) => !open)}
							className="inline-flex items-center gap-1 text-[#008782] hover:underline"
							aria-expanded={showSkipped}
						>
							{showSkipped ? (
								<ChevronDown className="w-3 h-3" />
							) : (
								<ChevronRight className="w-3 h-3" />
							)}
							{run.skipped.length} skipped
						</button>
					) : (
						<span className="text-text-muted">None</span>
					)}
				</td>
				<td className="px-3 py-2 text-sm text-text-muted whitespace-nowrap">
					{formatTimestamp(run.started_at_ms)}
				</td>
				<td className="px-3 py-2 text-sm text-text-muted">
					{formatDuration(run)}
				</td>
				<td className="px-3 py-2 text-right">
					{canCancel && (
						<button
							type="button"
							onClick={() => onCancelRun?.(run.id)}
							disabled={run.cancel_requested}
							title="Stop this run after the file it is reading"
							className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
						>
							<XCircle className="w-3 h-3" aria-hidden />
							{run.cancel_requested ? "Cancelling…" : "Cancel"}
						</button>
					)}
					{onRestoreRun &&
						(confirmingRestore ? (
							<span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-text-muted">
								{restoring
									? "Restoring…"
									: "Show the results as of this run?"}
								<button
									type="button"
									onClick={() => void restore()}
									disabled={restoring}
									className="rounded-md bg-[#008782] px-2 py-1 font-medium text-white hover:bg-[#00726e] disabled:opacity-60"
								>
									Restore
								</button>
								<button
									type="button"
									onClick={() => setConfirmingRestore(false)}
									disabled={restoring}
									className="rounded-md px-2 py-1 font-medium text-text-muted hover:bg-gray-100 disabled:opacity-60"
								>
									Cancel
								</button>
							</span>
						) : (
							<button
								type="button"
								onClick={() => setConfirmingRestore(true)}
								title="Set the table to what it showed when this run finished"
								className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-[#008782] hover:bg-[#008782]/10"
							>
								<Undo2 className="w-3 h-3" aria-hidden />
								Restore this version
							</button>
						))}
				</td>
			</tr>
			{showSkipped && (
				<tr className="bg-gray-50">
					<td colSpan={8} className="px-3 py-2">
						<ul className="space-y-1">
							{run.skipped.map((entry) => (
								<li key={entry.file} className="text-xs text-text-muted">
									<span className="font-mono">{entry.file}</span> — {entry.reason}
								</li>
							))}
						</ul>
					</td>
				</tr>
			)}
			{showError && run.error && (
				<tr className="bg-red-50">
					<td colSpan={8} className="px-3 py-2 text-xs text-red-700">
						{run.error}
					</td>
				</tr>
			)}
		</>
	);
};

const RunHistoryTable: React.FC<RunHistoryTableProps> = ({
	runs,
	onCancelRun,
	onRestoreRun,
}) => {
	if (runs.length === 0) {
		return (
			<p className="text-sm text-text-muted px-3 py-2">
				This workflow hasn't run yet.
			</p>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full">
				<thead>
					<tr className="text-left">
						{[
							"Status",
							"Trigger",
							"Files",
							"Values",
							"Skipped",
							"Started",
							"Took",
						].map((heading) => (
							<th
								key={heading}
								className="px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wider"
							>
								{heading}
							</th>
						))}
						{/* The actions column carries no visible label — the button
						    names itself, and a heading would only add noise. */}
						<th className="px-3 py-2">
							<span className="sr-only">Actions</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{runs.map((run) => (
						<RunRow
							key={run.id}
							run={run}
							onCancelRun={onCancelRun}
							onRestoreRun={run.can_restore ? onRestoreRun : undefined}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default RunHistoryTable;
